// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";
import { doc } from "@firebase/firestore";

// TYPES
import { DocumentRepresentativeType, PitbullCountry, Step } from "../../types";

// UTILS
import { getParsedRenaperData, onContinueRenaper } from "../../app/utils";
import {
  hasRenaperData,
  checkIsEmailAvailable,
  getDniFromCuit,
  isStepOneSent,
} from "../../utils";
import { documentTypesParsed, firestoreCollections } from "../../constants";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import { sendBusinessRepresentativeDocument } from "../../connectors/connectors";

// COMPONENTS
import { Icon } from "@allaria-tech/matter/Icon";
import LivenessValidationCard from "../../components/LivenessValidationCard/LivenessValidationCard";
import { hideLivenessStepHeader } from "../../components/LivenessValidationCard/utils/utils";
import UploadDniCard from "../../components/UploadDniCard/UploadDniCard";

import { AppDispatch, RootState } from "../../app/store";
import { fieldsFrom } from "../../inputs";

const identityJuridicalStep = (
  countries: PitbullCountry[],
): Omit<Step, "step"> => {
  const uploadDniCard = (
    dispatch: AppDispatch,
    state: RootState | undefined,
    formik: FormikProps<FormikValues> | undefined,
    currentDniStep?: "qr" | "upload_front" | "upload_back",
  ) => (
    <UploadDniCard
      dispatch={dispatch}
      state={state}
      formik={formik}
      currentDniStep={currentDniStep}
      getDniRef={({ db, uuid }) => doc(db, firestoreCollections.dni, uuid)}
      onUploadDni={async (type, file, uuid, cuit) => {
        await sendBusinessRepresentativeDocument({
          draftUUID: uuid,
          documentType: documentTypesParsed[type] as DocumentRepresentativeType,
          file,
          cuit: cuit,
        });
      }}
      showCopyLink
    />
  );

  const substeps: Omit<Step, "step">[] = [
    {
      name: "Validación",
      titleOfStep: "Vamos a verificar tu DNI",
      label: "uploadDNI", // Used to identify the step in remoteSteps
      component: (dispatch, state, formik) =>
        uploadDniCard(dispatch, state, formik),
      insideForm: true,
      onContinue: async (values, dispatch, state) => {
        if (!state || !dispatch) return;

        const { completedRemote } = state.onboardingData;
        if (!completedRemote) return;
        if (!hasRenaperData(state)) return;

        const informationParsed = state.renaper;

        await checkIsEmailAvailable({
          dispatch,
          idNumber:
            values.identityDocument ||
            state.renaper.number ||
            state.renaper.number ||
            getDniFromCuit(state.renaper.cuil || values.cuit),
        });

        const body = getParsedRenaperData(
          values,
          state,
          informationParsed,
          countries,
          "representativesAddresses",
        );

        dispatch(onUpdateFormData({ ...body }));

        return {
          newValuesToForm: body,
        };
      },
    },
    {
      titleOfStep: "Cargá fotos del frente de tu DNI",
      component: (dispatch, state, formik) =>
        uploadDniCard(dispatch, state, formik, "upload_front"),
      skipWhen: (_, state) => state.onboardingData.completedRemote,
      insideForm: true,
      onContinue: async (values, dispatch, state) => {
        if (!state || !dispatch) return;

        const { completedRemote } = state.onboardingData;
        if (!completedRemote) return;
        if (!hasRenaperData(state)) return;

        const informationParsed = state.renaper;

        await checkIsEmailAvailable({
          dispatch,
          idNumber:
            values.identityDocument ||
            state.renaper.number ||
            state.renaper.number ||
            getDniFromCuit(state.renaper.cuil || values.cuit),
        });

        const body = getParsedRenaperData(
          values,
          state,
          informationParsed,
          countries,
          "representativesAddresses",
        );

        dispatch(onUpdateFormData({ ...body }));

        return {
          newValuesToForm: body,
        };
      },
    },
    {
      titleOfStep: "Cargá fotos del dorso de tu DNI",
      component: (dispatch, state, formik) =>
        uploadDniCard(dispatch, state, formik, "upload_back"),
      skipWhen: (_, state) => state.onboardingData.completedRemote,
      onContinue: async (values, dispatch, state) => {
        if (!state || !dispatch) return;

        if (!hasRenaperData(state)) return;

        await checkIsEmailAvailable({
          dispatch,
          idNumber:
            values.identityDocument ||
            state.renaper.number ||
            state.renaper.number ||
            getDniFromCuit(state.renaper.cuil || values.cuit),
        });

        const informationParsed = state.renaper;

        const body = getParsedRenaperData(
          values,
          state,
          informationParsed,
          countries,
          "representativesAddresses",
        );

        dispatch(onUpdateFormData({ ...body }));

        return {
          newValuesToForm: body,
        };
      },
      insideForm: true,
    },
    {
      titleOfStep: "Información de tu DNI",
      fields: fieldsFrom(
        [
          "identityDocument",
          {
            disabled: true,
            tooltip: {
              label: <Icon name="edit" />,
              modal: "modal-edit-dni",
              external: true,
            },
          },
        ],
        "tramitNumber",
        "gender",
      ),
      skipWhen: (_, state) => hasRenaperData(state),
      onContinue: async (values: FormikValues, dispatch, state) => {
        if (!dispatch || !values || !state) return;

        return await onContinueRenaper(
          values,
          dispatch,
          state,
          countries,
          "representativesAddresses",
        );
      },
      disableBackNavigation: true,
      disableButtonBack: true,
    },
    {
      label: "livenessValidation", // Used to identify the step in remoteSteps
      titleOfStep: "Validá tu identidad",
      hideFormHeader: hideLivenessStepHeader,
      component: (dispatch, state, formik) => (
        <LivenessValidationCard
          dispatch={dispatch}
          state={state}
          formik={formik}
          getLivenessRef={({ db, uuid }) =>
            doc(db, firestoreCollections.liveness, uuid)
          }
          showCopyLink
        />
      ),
      insideForm: true,
    },
  ];

  return {
    label: "identity",
    name: "Validación",
    skipWhen: (_, state) => isStepOneSent(state),
    disableBackNavigation: true,
    disableButtonBack: true,
    hideFormHeaderOnMobile: true,
    subSteps: substeps.map((substep, i) => ({
      step: i + 1,
      ...substep,
    })),
  };
};

export default identityJuridicalStep;
